/*
 * Rollback: delete this file and remove the three <script>navigationGuard.init</script> lines from the three templates.
 */

(function() {
  'use strict';

  // Ensure namespace exists
  window.personalised = window.personalised || {};

  let initialized = {};
  let syncActive = false;
  let savedIntent = null; // { type: 'web'|'native', key?: string, href: string }
  let timeoutId = null;
  let removeTrap = null;
  let beforeUnloadHandler = null;
  let navigated = false;

  function isCartPage() {
    // Check via template name if available
    if (typeof window.template !== 'undefined' && window.template?.name === 'cart') {
      return true;
    }
    // Fallback to URL pattern using centralised route
    var cartPath = (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_url) || '/cart';
    return window.location.pathname === cartPath;
  }

  function isPersonalisedPage(rootSelector) {
    return document.querySelector(rootSelector) !== null;
  }

  function clearNavigationState() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    savedIntent = null;
    navigated = false;
    if (window.personalised?.modalSpinner) {
      window.personalised.modalSpinner.hide();
    }
  }

  function proceedWithNavigation() {
    if (navigated) return;

    // Remove beforeunload BEFORE marking navigated or initiating navigation.
    // Our own intercepted navigation is intentional — the user should not see the
    // native browser leave-page warning for a guarded internal page transition.
    // Real refresh / tab-close / window-close protection is unaffected: those fire
    // the browser's beforeunload directly and never reach this function.
    removeBeforeUnload();

    navigated = true;
    
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    
    function cleanupOnUnload() {
      clearNavigationState();
      window.removeEventListener('pagehide', cleanupOnUnload);
      window.removeEventListener('beforeunload', cleanupOnUnload);
    }
    
    // rationale: prevent pre-nav hide - only cleanup on pagehide, not beforeunload
    window.addEventListener('pagehide', cleanupOnUnload, { once: true });
    
    if (savedIntent) {
      if (savedIntent.type === 'back') {
        // Back button: go back in history
        history.back();
      } else if (savedIntent.type === 'native' && window.tapcartNavigation) {
        // Recreate the originally intended native navigation
        window.tapcartNavigation.tryNavigate(savedIntent.key, savedIntent.href);
      } else {
        // Existing behavior: web navigation
        const href = savedIntent.href;
        // rationale: prevent pre-nav hide - set navigating flag
        if (window.personalised?.modalSpinner?.setNavigating) {
          window.personalised.modalSpinner.setNavigating(true);
        }
        if (window.personalised?.modalSpinner?.show) {
          window.personalised.modalSpinner.show('Loading your pack…');
        }
        window.location.assign(href);
      }
    }
  }

  function handleTimeout() {
    console.warn('[Navigation Guard] Sync timeout reached, proceeding with navigation');
    if (window.personalised?.eventBus?.emit) {
      window.personalised.eventBus.emit('navProceedAfterTimeout', { intent: savedIntent });
    }
    proceedWithNavigation();
  }

  function interceptNavigation(intent, message) {
    if (savedIntent) return; // Already intercepting

    savedIntent = intent;
    
    if (window.personalised?.modalSpinner) {
      window.personalised.modalSpinner.show(message || 'Loading your pack...');
    }

    timeoutId = setTimeout(handleTimeout, 5000);
  }

  function setupBeforeUnload() {
    if (beforeUnloadHandler) return;

    // PR-2.1: also block page-unload during the pending/debounce window (syncPending).
    beforeUnloadHandler = function(e) {
      const isBlocked = syncActive || window.personalised?.cartSyncState?.syncPending === true;
      if (isBlocked) {
        const message = 'Your pack is being updated. Are you sure you want to leave?';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
  }

  function removeBeforeUnload() {
    if (beforeUnloadHandler) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      beforeUnloadHandler = null;
    }
  }

  function handleSyncStarted() {
    syncActive = true;
    setupBeforeUnload();
  }

  // PR-2.1: syncPendingStarted fires the moment the outer debounce is first triggered
  // (before syncStarted, which only fires when the debounce actually fires).
  // Arming beforeunload here closes the navigation gap during the debounce window.
  function handleSyncPendingStarted() {
    setupBeforeUnload();
  }

  // PR-2.1: syncCompleted means one operation finished, NOT that all work is done.
  // Only release navigation and proceed when syncPending is also false
  // (no coalesced follow-up sync is still required).
  function handleSyncCompleted() {
    syncActive = false;
    if (!window.personalised?.cartSyncState?.syncPending) {
      removeBeforeUnload();
      if (savedIntent) {
        proceedWithNavigation();
      }
    }
    // If syncPending is still true, a follow-up sync is about to start;
    // do not release — _startSyncCycle will emit the follow-up syncStarted momentarily.
  }

  // PR-2.1: on failure the dirty cycle has already been fully cleared by _startSyncCycle
  // before syncFailed is emitted. syncPending is false by the time this handler runs.
  // For immediate-path syncFailed (from _executeCartRefresh guards), syncPending is never
  // set, so the check is also safe.
  function handleSyncFailed() {
    syncActive = false;
    if (!window.personalised?.cartSyncState?.syncPending) {
      removeBeforeUnload();
      if (savedIntent) {
        proceedWithNavigation();
      }
    }
  }

  function setupEventListeners(rootSelector) {
    // Subscribe to sync events
    if (window.personalised?.eventBus?.on) {
      window.personalised.eventBus.on('syncStarted', handleSyncStarted);
      window.personalised.eventBus.on('syncCompleted', handleSyncCompleted);
      window.personalised.eventBus.on('syncFailed', handleSyncFailed);
      // PR-2.1: arm beforeunload during the debounce/pending window before syncStarted fires.
      window.personalised.eventBus.on('syncPendingStarted', handleSyncPendingStarted);
    }

    // Set initial sync state (handles page re-entry while a sync is in flight)
    syncActive = window.personalised?.cartSyncState?.syncInProgress === true;
    const hasPending = window.personalised?.cartSyncState?.syncPending === true;
    if (syncActive || hasPending) {
      setupBeforeUnload();
    }

    // Delegated click handler
    function handleClick(e) {
      // Early exit if not on personalised page
      if (!isPersonalisedPage(rootSelector)) return;

      // Early exit if cart page
      if (isCartPage()) return;

      const oneOffEntry = e.target.closest('[data-oneoff-entry]');
      if (oneOffEntry) return;

      // PR-2.1: block navigation if sync is active OR if pack state is dirty and sync is pending.
      const syncActiveOrPending = syncActive || window.personalised?.cartSyncState?.syncPending === true;
      if (!syncActiveOrPending) return;

      const target = e.target.closest('a[href]');
      const buttonTarget = e.target.closest('#checkout-button-desktop, #checkout-button-mobile, button[data-checkout], button[data-cart], button[id*="checkout"], button[class*="checkout"], button[id*="cart"], button[class*="cart"], .cart-drawer-button, .personalised-review-v2-back-btn, .personalised-review-back-btn-mobile, .personalised-review-back-btn-desktop, [data-personalised-header-library-back]');

      if (!target && !buttonTarget) return;

      // Don't intercept if inside a form (unless it's a navigation button)
      if (target && target.closest('form')) return;

      // Handle button targets (checkout/cart/back buttons)
      if (buttonTarget) {
        e.preventDefault();
        e.stopPropagation();

        // Back button: defer history.back() until sync completes
        if (buttonTarget.classList.contains('personalised-review-v2-back-btn') ||
            buttonTarget.classList.contains('personalised-review-back-btn-mobile') ||
          buttonTarget.classList.contains('personalised-review-back-btn-desktop') ||
          buttonTarget.hasAttribute('data-personalised-header-library-back')) {
          const intent = { type: 'back' };
          interceptNavigation(intent, 'Saving your pack...');
          return;
        }

        const cartUrl = (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_url) || '/cart';
        const href = buttonTarget.dataset.href || 
                    buttonTarget.closest('form')?.getAttribute('action') || 
                    cartUrl;
        
        const intent = { type: 'web', href };
        interceptNavigation(intent, 'Loading your pack...');
        return;
      }

      // Handle link targets
      const href = target.getAttribute('href');
      if (!href || href.startsWith('javascript:') || href.startsWith('tel:') || href.startsWith('mailto:')) {
        return;
      }

      // Detect native intent if the target has data-tapcart-nav-key and Tapcart is available
      const tapcartKey = target.getAttribute && target.getAttribute('data-tapcart-nav-key');
      const inTapcart = !!(window.tapcartNavigation); // simple presence check
      
      const intent = (tapcartKey && inTapcart)
        ? { type: 'native', key: tapcartKey, href }
        : { type: 'web', href };

      // Check for cart links (skip personalised review links)
      var cartUrl = (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_url) || '/cart';
      if ((href.includes(cartUrl) || target.classList.contains('cart-drawer-button')) && 
          !target.hasAttribute('data-personalised-review-link')) {
        e.preventDefault();
        e.stopPropagation();
        interceptNavigation(intent, 'Loading your pack...');
        return;
      }

      // Handle all other links (including modified clicks)
      e.preventDefault();
      e.stopPropagation();
      interceptNavigation(intent, 'Loading your pack...');
    }

    document.addEventListener('click', handleClick, true);

    // Cleanup on page hide
    function cleanup() {
      if (window.personalised?.eventBus?.off) {
        window.personalised.eventBus.off('syncStarted', handleSyncStarted);
        window.personalised.eventBus.off('syncCompleted', handleSyncCompleted);
        window.personalised.eventBus.off('syncFailed', handleSyncFailed);
        window.personalised.eventBus.off('syncPendingStarted', handleSyncPendingStarted);
      }
      document.removeEventListener('click', handleClick, true);
      removeBeforeUnload();
      clearNavigationState();
    }

    window.addEventListener('pagehide', cleanup);

    return cleanup;
  }

  function init({ rootSelector, pageKind }) {
    // Idempotent: do nothing if already initialized for this pageKind
    if (initialized[pageKind]) {
      return;
    }

    console.info('[Navigation Guard] Initializing for:', pageKind);
    
    const cleanup = setupEventListeners(rootSelector);
    initialized[pageKind] = { cleanup };
  }

  // Export to namespace
  window.personalised.navigationGuard = {
    init: init
  };

})();

/*
 * Acceptance Tests:
 * 
 * 1. Start sync → click link (internal/external/hash/cart/checkout) → spinner appears → nav proceeds on syncCompleted or after 5s → spinner hides → no double navigation.
 * 2. Forms submit unaffected during sync.
 * 3. Modified clicks are intercepted (per policy) and resume after sync/timeout.
 * 4. beforeunload shows while sync active.
 * 5. Works on personalised-product/library/review; not active on cart page.
 * 6. A11y: dialog role, aria label, focus trapped; focus restored.
 */
